import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabaseClient';
import { useNavigate, Link } from 'react-router-dom';
import SafeIcon from '../../common/SafeIcon';
import { TEAM_K9_LOGO_URL, FALLBACK_LOGO_URL } from '../../common/assets';

const TrainerLogin = () => {
  const [authStep, setAuthStep] = useState('login'); // 'login' | 'access-code'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [trainerProfile, setTrainerProfile] = useState(null);
  
  const [accessCode, setAccessCode] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [logoError, setLogoError] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    // Check if user is already authenticated
    const checkAuth = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        // Check if trainer has a profile
        const { data: profile } = await supabase
          .from('trainer_profiles')
          .select('*')
          .eq('user_id', user.id)
          .single();
        
        if (profile) {
          setTrainerProfile(profile);
          // Check if access code is verified in session
          const sessionCodeVerified = sessionStorage.getItem('trainerAccessVerified') === 'true';
          if (sessionCodeVerified) {
            // Already verified, redirect to dashboard
            navigate('/dashboard');
          } else {
            setAuthStep('access-code');
          }
        }
      }
    };
    checkAuth();
  }, [navigate]);

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const { data: { user }, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (signInError) {
        console.error('Supabase sign-in error:', signInError);
        setError(signInError.message);
        return;
      }

      if (!user) {
        setError('Login failed');
        return;
      }

      // Check for trainer profile
      const { data: profile, error: profileError } = await supabase
        .from('trainer_profiles')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (profileError && profileError.code !== 'PGRST116') {
        throw profileError;
      }

      if (!profile) {
        // No profile found, redirect to complete profile
        navigate('/complete-trainer-profile');
        return;
      }

      setTrainerProfile(profile);
      setAuthStep('access-code');
    } catch (error) {
      console.error('Login error:', error);
      setError(error.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleAccessCodeVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (accessCode.length !== 4) {
      setError('Please enter a 4-digit access code');
      setLoading(false);
      return;
    }

    try {
      if (trainerProfile.access_code !== accessCode) {
        setError('Invalid access code');
        setLoading(false);
        return;
      }

      // Store verification in session
      sessionStorage.setItem('trainerAccessVerified', 'true');
      sessionStorage.setItem('trainerProfileId', trainerProfile.id);
      
      // Redirect to dashboard
      navigate('/dashboard');
    } catch (error) {
      console.error('Access code verification error:', error);
      setError('Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    sessionStorage.removeItem('trainerAccessVerified');
    sessionStorage.removeItem('trainerProfileId');
    setAuthStep('login');
    setTrainerProfile(null);
    setEmail('');
    setPassword('');
    setAccessCode('');
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <div className="flex-1 flex items-center justify-center p-4 sm:p-6">
        <div className="w-full max-w-md">
          {/* Logo/Branding */}
          <div className="text-center mb-8">
            <img 
              src={logoError ? FALLBACK_LOGO_URL : TEAM_K9_LOGO_URL} 
              alt="Team K9 Logo" 
              className="w-32 h-32 sm:w-36 sm:h-36 mx-auto mb-6 object-contain"
              onError={() => setLogoError(true)}
            />
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">Team K9 Training</h1>
            <p className="text-gray-600 text-base sm:text-lg">Trainer Portal</p>
          </div>

          {/* Login Card */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6 sm:p-8">
            {authStep === 'login' ? (
              <>
                <h2 className="text-xl font-semibold text-gray-800 mb-6">Sign In</h2>
                <form onSubmit={handleEmailLogin} className="space-y-5">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email
                    </label>
                    <input
                      id="email"
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="trainer@example.com"
                      className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all bg-gray-50 focus:bg-white outline-none text-base"
                    />
                  </div>
                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                      Password
                    </label>
                    <input
                      id="password"
                      type="password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter your password"
                      className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all bg-gray-50 focus:bg-white outline-none text-base"
                    />
                  </div>

                  {error && (
                    <div className="p-3 rounded-lg bg-red-50 text-red-600 text-sm flex items-start gap-2">
                      <SafeIcon name="AlertCircle" className="w-5 h-5 flex-shrink-0 mt-0.5" />
                      <span>{error}</span>
                    </div>
                  )}

                  <button 
                    type="submit" 
                    disabled={loading}
                    className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3.5 sm:py-4 rounded-xl shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-0.5 flex items-center justify-center gap-2 text-base disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        <span>Sign In</span>
                        <SafeIcon name="ArrowRight" className="w-4 h-4" />
                      </>
                    )}
                  </button>
                </form>

                {/* Request Access Link */}
                <div className="mt-6 text-center border-t border-gray-100 pt-6">
                  <p className="text-gray-600 text-sm mb-2">Don't have an account?</p>
                  <Link 
                    to="/request-account"
                    className="text-indigo-600 hover:text-indigo-800 font-medium text-sm flex items-center justify-center gap-1 group"
                  >
                    <span>Request Access</span>
                    <SafeIcon name="ArrowRight" className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                  </Link>
                </div>
              </>
            ) : (
              <>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-semibold text-gray-800">Verify Access Code</h2>
                  <button onClick={handleLogout} className="text-sm text-gray-500 hover:text-gray-700">
                    Sign Out
                  </button>
                </div>
                <p className="text-sm text-gray-600 mb-6">
                  Enter your 4-digit access code to continue to your dashboard.
                </p>
                <form onSubmit={handleAccessCodeVerify} className="space-y-5">
                  <div>
                    <label htmlFor="accessCode" className="block text-sm font-medium text-gray-700 mb-2">
                      Access Code
                    </label>
                    <input
                      id="accessCode"
                      type="text"
                      required
                      maxLength={4}
                      value={accessCode}
                      onChange={(e) => setAccessCode(e.target.value.replace(/\D/g, ''))}
                      placeholder="0000"
                      className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all bg-gray-50 focus:bg-white outline-none text-center text-2xl tracking-widest font-mono"
                    />
                  </div>

                  {error && (
                    <div className="p-3 rounded-lg bg-red-50 text-red-600 text-sm flex items-start gap-2">
                      <SafeIcon name="AlertCircle" className="w-5 h-5 flex-shrink-0 mt-0.5" />
                      <span>{error}</span>
                    </div>
                  )}

                  <button 
                    type="submit" 
                    disabled={loading || accessCode.length !== 4}
                    className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3.5 sm:py-4 rounded-xl shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-0.5 flex items-center justify-center gap-2 text-base disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        <span>Verify Code</span>
                        <SafeIcon name="CheckCircle" className="w-4 h-4" />
                      </>
                    )}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrainerLogin;